import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { CartFab, CartDrawer } from '../components/Cart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const QRItem = ({ restaurantIdOverride, slugOverride, tableNumberOverride, itemIdOverride }) => {
  const params = useParams();
  const restaurantId = params.restaurantId || restaurantIdOverride;
  const itemId = params.itemId || itemIdOverride;
  const tableNumber = params.tableNumber || tableNumberOverride;
  const navigate = useNavigate();
  const { t, lang, getLocalized } = useLanguage();
  const cart = useCart();
  const [item, setItem] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [itemId]);

  const fetchData = async () => {
    try {
      const [itemRes, restRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/menu/item/${itemId}`),
        axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}`),
      ]);
      setItem(itemRes.data);
      setRestaurant(restRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600"></div>
      </div>
    );
  }

  if (!item || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <p className="text-red-600 text-xl">Məhsul tapılmadı</p>
      </div>
    );
  }

  const currencySymbol = restaurant.currency_symbol || '₼';

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero image */}
      <div className="relative aspect-[16/10] sm:aspect-[16/8] max-h-[500px] bg-stone-200 overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={getLocalized(item, 'name')}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-400 text-9xl">
            🍽️
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white shadow-lg flex items-center justify-center transition"
          data-testid="back-button"
        >
          <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {item.is_featured && (
          <div className="absolute top-4 right-4 bg-amber-500 text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg">
            ★ {t('featured')}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 -mt-12 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          {tableNumber && (
            <div className="mb-3 inline-flex items-center gap-1.5 bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-bold px-2.5 py-1 rounded-full" data-testid="table-badge">
              🪑 Masa #{tableNumber}
            </div>
          )}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" data-testid="item-detail-name">
            {getLocalized(item, 'name')}
          </h1>

          <div className="flex items-baseline space-x-3 mb-4">
            {item.discount_price ? (
              <>
                <span className="text-3xl font-bold text-emerald-600">
                  {item.discount_price} {currencySymbol}
                </span>
                <span className="text-xl line-through text-gray-400">
                  {item.price} {currencySymbol}
                </span>
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  {t('discount')}: {Math.round(((item.price - item.discount_price) / item.price) * 100)}%
                </span>
              </>
            ) : (
              <span className="text-3xl font-bold text-emerald-600">
                {item.price} {currencySymbol}
              </span>
            )}
          </div>

          {getLocalized(item, 'description') && (
            <p className="text-gray-700 leading-relaxed text-base md:text-lg" data-testid="item-detail-description">
              {getLocalized(item, 'description')}
            </p>
          )}

          {restaurant.service_fee_percentage > 0 && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              💡 {t('service_fee')}: {restaurant.service_fee_percentage}%
            </div>
          )}

          {/* ADD TO CART */}
          {(() => {
            const cartItem = cart.items.find((i) => i.item_id === item.id);
            const qty = cartItem?.quantity || 0;
            if (qty === 0) {
              return (
                <button
                  onClick={() => cart.addItem(item, 1)}
                  className="mt-6 w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-lg transition shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2"
                  data-testid="item-add-to-cart"
                >
                  🛒 Səbətə əlavə et
                </button>
              );
            }
            return (
              <div className="mt-6 flex items-center justify-between bg-emerald-50 border-2 border-emerald-300 rounded-xl p-2">
                <button
                  onClick={() => cart.updateQuantity(item.id, -1)}
                  className="w-12 h-12 rounded-lg bg-white hover:bg-stone-100 text-stone-700 font-bold text-xl shadow"
                  data-testid="item-qty-minus"
                >−</button>
                <span className="text-2xl font-bold text-emerald-700" data-testid="item-qty-display">{qty}</span>
                <button
                  onClick={() => cart.addItem(item, 1)}
                  className="w-12 h-12 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xl shadow"
                  data-testid="item-qty-plus"
                >＋</button>
              </div>
            );
          })()}

          <Link
            to={
              slugOverride
                ? (tableNumber ? `/${slugOverride}/masa${tableNumber}` : `/${slugOverride}`)
                : (tableNumber ? `/qr-menu/${restaurantId}/t/${tableNumber}` : `/qr-menu/${restaurantId}`)
            }
            className="mt-4 block w-full text-center bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold py-3 rounded-xl transition"
            data-testid="back-to-menu-button"
          >
            ← {t('menu')}
          </Link>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500 pb-8">
          <p className="font-bold text-gray-700">{restaurant.name}</p>
          <p>📍 {restaurant.address}</p>
          <p>📞 {restaurant.phone}</p>
        </div>
      </div>

      <CartFab lang={lang} />
      <CartDrawer restaurant={restaurant} lang={lang} />
    </div>
  );
};
